import React, {useEffect, useRef, useState} from "react"
import FirmwareUploadSection from "../FirmwareUploadSection"
import CiBuildSection from "../CiBuildSection"
import {ClearAlarmsResult} from "../../store/iotShadowStore"
import {useIsAdmin, useIsDeveloper, useIsReadOnly} from "../../store/authStore"

interface ManualControlsProps {
	isLoading: boolean
	connectivityLoading: boolean
	firmwareUploadLoading: boolean
	firmwareUploadSuccess: string | null
	firmwareUploadError: string | null
	selectedFile: File | null
	selectedDevices: string[]
	rtlUploadLoading: boolean
	rtlUploadSuccess: string | null
	rtlUploadError: string | null
	selectedRtlFile: File | null
	onRefreshDevices: () => void
	onRefreshConnectivity: () => void
	onClearAll: () => void
	onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
	onFileUpload: () => void
	onClearFirmwareStatus: () => void
	onRtlFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
	onRtlFileUpload: () => void
	/** RTL upload of a CI build (S3 key) to the single selected device. */
	onRtlCiUpload: (key: string) => void
	/** Alarms of the selected devices are being cleared before this upload starts. */
	preUploadClearing: "firmware" | "rtl" | null
	/** Type of the selected device when exactly one is selected, else null. */
	singleSelectedDeviceType: "angel" | "falcon" | "unknown" | null
	onClearRtlStatus: () => void
	onCancelQueued: () => Promise<void>
	onCancelInProgress: () => Promise<void>
	onCancelDeviceJobs: (deviceIds: string[]) => Promise<void>
	onClearAllAlarms: (deviceIds: string[]) => Promise<ClearAlarmsResult>
}

const ManualControls: React.FC<ManualControlsProps> = ({
	isLoading,
	connectivityLoading,
	firmwareUploadLoading,
	firmwareUploadSuccess,
	firmwareUploadError,
	selectedFile,
	selectedDevices,
	rtlUploadLoading,
	rtlUploadSuccess,
	rtlUploadError,
	selectedRtlFile,
	onRefreshDevices,
	onRefreshConnectivity,
	onClearAll,
	onFileSelect,
	onFileUpload,
	onClearFirmwareStatus,
	onRtlFileSelect,
	onRtlFileUpload,
	onRtlCiUpload,
	preUploadClearing,
	singleSelectedDeviceType,
	onClearRtlStatus,
	onCancelQueued,
	onCancelInProgress,
	onCancelDeviceJobs,
	onClearAllAlarms,
}) => {
	// Developers may only act on their own devices: actions that hit ALL devices are hidden
	// (the backend refuses them with 403 as well).
	const isDeveloper = useIsDeveloper()
	// Read-only roles (viewer / viewer_all) get no write controls at all: no alarm clearing, no job
	// cancelling, no firmware/RTL upload. Refresh and the local "Clear All" stay.
	const isReadOnly = useIsReadOnly()
	// CI builds (Make Angel / Make Falcon) and RTL upload from S3: admin and developer only.
	const isAdmin = useIsAdmin()
	const canUseCi = !isReadOnly && (isAdmin || isDeveloper)
	const [clearAlarmsLoading, setClearAlarmsLoading] = useState(false)
	const [clearAlarmsMessage, setClearAlarmsMessage] = useState<string | null>(null)
	const clearAlarmsTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

	useEffect(() => () => {
		if (clearAlarmsTimer.current) clearTimeout(clearAlarmsTimer.current)
	}, [])

	// Shows a message and hides it after ms, cancelling the timer of any earlier message.
	const showClearAlarmsMessage = (message: string, ms: number) => {
		if (clearAlarmsTimer.current) clearTimeout(clearAlarmsTimer.current)
		setClearAlarmsMessage(message)
		clearAlarmsTimer.current = setTimeout(() => setClearAlarmsMessage(null), ms)
	}

	const handleClearAllAlarms = async () => {
		// Snapshot the selection so changing it while the request runs does not affect this run.
		const deviceIds = [...selectedDevices]
		if (deviceIds.length === 0) return
		const confirmed = window.confirm(
			`Clear alarms on ${deviceIds.length} selected device(s)?\n\n${deviceIds.join("\n")}\n\n` +
				"This sets in the general shadow of each selected device:\n" +
				"- desired.ARMED = 0 (intruder)\n" +
				"- desired.ANTITAMPER_RESET = 1, then 0 (antitamper)",
		)
		if (!confirmed) return

		setClearAlarmsLoading(true)
		if (clearAlarmsTimer.current) clearTimeout(clearAlarmsTimer.current)
		setClearAlarmsMessage(null)
		try {
			const result = await onClearAllAlarms(deviceIds)
			if (result.failed.length === 0) {
				showClearAlarmsMessage(`Alarms cleared on ${result.total} selected device(s)`, 5000)
			} else {
				const failedList = result.failed.map((f) => `${f.deviceId} (${f.error})`).join("; ")
				showClearAlarmsMessage(
					`Error: alarms cleared on ${result.succeeded.length} of ${result.total} device(s). Failed: ${failedList}`,
					30000,
				)
			}
		} catch (error: any) {
			showClearAlarmsMessage(`Error: ${error.message || "Failed to clear alarms"}`, 8000)
		} finally {
			setClearAlarmsLoading(false)
		}
	}

	const [cancelQueuedLoading, setCancelQueuedLoading] = useState(false)
	const [cancelInProgressLoading, setCancelInProgressLoading] = useState(false)
	const [cancelDeviceJobsLoading, setCancelDeviceJobsLoading] = useState(false)
	const [cancelQueuedMessage, setCancelQueuedMessage] = useState<string | null>(null)
	const [cancelInProgressMessage, setCancelInProgressMessage] = useState<string | null>(null)
	const [cancelDeviceJobsMessage, setCancelDeviceJobsMessage] = useState<string | null>(null)

	const handleCancelQueued = async () => {
		setCancelQueuedLoading(true)
		setCancelQueuedMessage(null)
		try {
			await onCancelQueued()
			setCancelQueuedMessage("All queued jobs have been canceled")
			setTimeout(() => setCancelQueuedMessage(null), 5000)
		} catch (error: any) {
			setCancelQueuedMessage(`Error: ${error.message || "Failed to cancel queued jobs"}`)
			setTimeout(() => setCancelQueuedMessage(null), 8000)
		} finally {
			setCancelQueuedLoading(false)
		}
	}

	const handleCancelInProgress = async () => {
		setCancelInProgressLoading(true)
		setCancelInProgressMessage(null)
		try {
			await onCancelInProgress()
			setCancelInProgressMessage("All in-progress jobs have been canceled")
			setTimeout(() => setCancelInProgressMessage(null), 5000)
		} catch (error: any) {
			setCancelInProgressMessage(`Error: ${error.message || "Failed to cancel in-progress jobs"}`)
			setTimeout(() => setCancelInProgressMessage(null), 8000)
		} finally {
			setCancelInProgressLoading(false)
		}
	}

	const handleCancelDeviceJobs = async () => {
		setCancelDeviceJobsLoading(true)
		setCancelDeviceJobsMessage(null)
		try {
			await onCancelDeviceJobs(selectedDevices)
			setCancelDeviceJobsMessage(
				`All jobs for ${selectedDevices.length} selected device(s) have been canceled`,
			)
			setTimeout(() => setCancelDeviceJobsMessage(null), 5000)
		} catch (error: any) {
			setCancelDeviceJobsMessage(
				`Error: ${error.message || "Failed to cancel jobs for selected devices"}`,
			)
			setTimeout(() => setCancelDeviceJobsMessage(null), 8000)
		} finally {
			setCancelDeviceJobsLoading(false)
		}
	}

	return (
		<div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 mb-6">
			<h2 className="font-semibold mb-3 text-gray-900 dark:text-white">Manual Controls</h2>

			{cancelQueuedMessage && (
				<div
					className={`mb-4 p-3 rounded ${
						cancelQueuedMessage.startsWith("Error")
							? "bg-red-100 dark:bg-red-900/40 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300"
							: "bg-green-100 dark:bg-green-900/40 border border-green-400 dark:border-green-700 text-green-700 dark:text-green-300"
					}`}>
					{cancelQueuedMessage}
				</div>
			)}

			{cancelInProgressMessage && (
				<div
					className={`mb-4 p-3 rounded ${
						cancelInProgressMessage.startsWith("Error")
							? "bg-red-100 dark:bg-red-900/40 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300"
							: "bg-green-100 dark:bg-green-900/40 border border-green-400 dark:border-green-700 text-green-700 dark:text-green-300"
					}`}>
					{cancelInProgressMessage}
				</div>
			)}

			{clearAlarmsMessage && (
				<div
					className={`mb-4 p-3 rounded ${
						clearAlarmsMessage.startsWith("Error")
							? "bg-red-100 dark:bg-red-900/40 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300"
							: "bg-green-100 dark:bg-green-900/40 border border-green-400 dark:border-green-700 text-green-700 dark:text-green-300"
					}`}>
					{clearAlarmsMessage}
				</div>
			)}

			{cancelDeviceJobsMessage && (
				<div
					className={`mb-4 p-3 rounded ${
						cancelDeviceJobsMessage.startsWith("Error")
							? "bg-red-100 border border-red-400 text-red-700"
							: "bg-green-100 border border-green-400 text-green-700"
					}`}>
					{cancelDeviceJobsMessage}
				</div>
			)}

			<div className="flex gap-4 items-center flex-wrap mb-4">
				<button
					onClick={onRefreshDevices}
					disabled={isLoading}
					className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed">
					{isLoading ? "Refreshing..." : "Refresh All Data"}
				</button>
				<button
					onClick={onRefreshConnectivity}
					disabled={connectivityLoading}
					className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed">
					{connectivityLoading ? "Refreshing..." : "Refresh Connectivity"}
				</button>
				<button
					onClick={onClearAll}
					className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded">
					Clear All
				</button>
				{!isDeveloper && !isReadOnly && (
					<>
						<button
							onClick={handleCancelQueued}
							disabled={cancelQueuedLoading}
							className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed">
							{cancelQueuedLoading ? "Canceling..." : "Cancel All Queued Jobs"}
						</button>
						<button
							onClick={handleCancelInProgress}
							disabled={cancelInProgressLoading}
							className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed">
							{cancelInProgressLoading ? "Canceling..." : "Cancel All In-Progress Jobs"}
						</button>
					</>
				)}
				{!isReadOnly && (
					<>
						<button
							onClick={handleClearAllAlarms}
							disabled={clearAlarmsLoading || selectedDevices.length === 0}
							title={
								selectedDevices.length === 0
									? "Select devices in the table first"
									: `Set desired.ARMED = 0 and pulse desired.ANTITAMPER_RESET (1, then 0) in the general shadow of ${selectedDevices.length} selected device(s)`
							}
							className="px-4 py-2 bg-rose-700 hover:bg-rose-800 text-white rounded disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed">
							{clearAlarmsLoading
								? "Clearing alarms..."
								: `Clear Alarms for Selected${selectedDevices.length > 0 ? ` (${selectedDevices.length})` : ""}`}
						</button>
						<div className="ml-auto">
							<button
								onClick={handleCancelDeviceJobs}
								disabled={cancelDeviceJobsLoading || selectedDevices.length === 0}
								title={
									selectedDevices.length === 0
										? "Select devices in the table first"
										: `Cancel all jobs (queued, in-progress, failed, succeeded) for ${selectedDevices.length} selected device(s)`
								}
								className="px-4 py-2 bg-red-700 text-white rounded hover:bg-red-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm">
								{cancelDeviceJobsLoading
									? "Canceling..."
									: `Cancel Jobs for Selected${selectedDevices.length > 0 ? ` (${selectedDevices.length})` : ""}`}
							</button>
						</div>
					</>
				)}
			</div>
			{canUseCi && <CiBuildSection />}
			{!isReadOnly && (
				<FirmwareUploadSection
					firmwareUploadLoading={firmwareUploadLoading}
					firmwareUploadSuccess={firmwareUploadSuccess}
					firmwareUploadError={firmwareUploadError}
					selectedFile={selectedFile}
					selectedDevices={selectedDevices}
					requireSelection={isDeveloper}
					onFileSelect={onFileSelect}
					onFileUpload={onFileUpload}
					onClearFirmwareStatus={onClearFirmwareStatus}
					rtlUploadLoading={rtlUploadLoading}
					rtlUploadSuccess={rtlUploadSuccess}
					rtlUploadError={rtlUploadError}
					selectedRtlFile={selectedRtlFile}
					onRtlFileSelect={onRtlFileSelect}
					onRtlFileUpload={onRtlFileUpload}
					onRtlCiUpload={onRtlCiUpload}
					preUploadClearing={preUploadClearing}
					singleSelectedDeviceType={singleSelectedDeviceType}
					allowCiSource={canUseCi}
					onClearRtlStatus={onClearRtlStatus}
				/>
			)}
		</div>
	)
}

export default ManualControls
